// src/pages/legal/LegalNotice.js
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import {
  Business as BusinessIcon,
  Copyright as CopyrightIcon,
  Language as LanguageIcon,
  ContactMail as ContactMailIcon,
  Web as WebIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';

// Paleta de colores verde pastel
const Colors = {
  primary: '#7FB069',
  primaryLight: '#A8D5BA',
  primaryDark: '#6B8E5A',
  secondary: '#000000',
  accent: '#90C695',
  background: '#F0F8F0',
  surface: '#FFFFFF',
  text: '#000000',
  textSecondary: '#724CA7'
};

const LegalNotice = () => {
  return (
    <Box sx={{ flexGrow: 1, bgcolor: Colors.background, minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `2px solid ${Colors.primaryLight}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AccountBalanceIcon sx={{ fontSize: 40, color: Colors.primary, mr: 2 }} />
            <Typography variant="h3" sx={{ color: Colors.text, fontWeight: 'bold' }}>
              Aviso Legal
            </Typography>
          </Box>
          <Typography variant="h6" color={Colors.textSecondary} paragraph>
            AMPA del Bosc - Información Legal y Corporativa
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Fecha de última actualización:</strong> {new Date().toLocaleDateString('es-ES')}
            </Typography>
          </Alert>
        </Paper>

        {/* Identificación del Titular */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 2 }} />
            1. Identificación del Titular
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            En cumplimiento de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio Electrónico, se informa:
          </Typography>

          <Box sx={{ bgcolor: Colors.background, p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
              Datos Identificativos
            </Typography>
            <Stack spacing={1}>
              <Typography><strong>Denominación social:</strong> Asociación de Madres y Padres de Alumnos del Bosc</Typography>
              <Typography><strong>Nombre comercial:</strong> AMPA del Bosc</Typography>
              <Typography><strong>CIF:</strong> G-[Número CIF]</Typography>
              <Typography><strong>Domicilio social:</strong> [Dirección completa de la escuela]</Typography>
              <Typography><strong>Código postal:</strong> [CP] [Ciudad], [Provincia]</Typography>
              <Typography><strong>País:</strong> España</Typography>
            </Stack>
          </Box>

          <Box sx={{ bgcolor: Colors.background, p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
              Datos de Contacto
            </Typography>
            <Stack spacing={1}>
              <Typography><strong>Teléfono:</strong> [Teléfono principal]</Typography>
              <Typography><strong>Email:</strong> ampadelbosc@gmail.com</Typography>
              <Typography><strong>Sitio web:</strong> www.ampaelbosc.edu</Typography>
              <Typography><strong>Horario de atención:</strong> Lunes a viernes de 9:00 a 17:00h</Typography>
            </Stack>
          </Box>

          <Box sx={{ bgcolor: Colors.background, p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
              Registro y Autorizaciones
            </Typography>
            <Stack spacing={1}>
              <Typography><strong>Registro de Asociaciones:</strong> Sección [X], Número [XXXX]</Typography>
              <Typography><strong>Fecha de constitución:</strong> [Fecha de constitución]</Typography>
              <Typography><strong>Ámbito territorial:</strong> Autonómico - Andalucía</Typography>
              <Typography><strong>Régimen jurídico:</strong> Ley Orgánica 1/2002 de Asociaciones</Typography>
            </Stack>
          </Box>
        </Paper>

        {/* Objeto Social */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <WebIcon sx={{ mr: 2 }} />
            2. Objeto Social y Finalidades
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            La AMPA del Bosc es una asociación sin ánimo de lucro cuyo objeto social comprende:
          </Typography>

          <Stack spacing={2}>
            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                🎯 Finalidad Principal
              </Typography>
              <Typography variant="body2">
                Representar a las madres y padres de alumnos en sus relaciones con el centro educativo y facilitar su participación en las actividades del mismo, así como promover y desarrollar actividades complementarias para el alumnado.
              </Typography>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                📋 Actividades Específicas
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• Gestión de servicios complementarios (comedor, acogida, extraescolares)" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Organización de actividades culturales, deportivas y lúdicas" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Representación de las familias ante la dirección del centro" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Colaboración en la mejora de las instalaciones educativas" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Fomento de la participación familiar en la comunidad educativa" />
                </ListItem>
              </List>
            </Paper>
          </Stack>
        </Paper>

        {/* Condiciones de Uso del Sitio Web */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <LanguageIcon sx={{ mr: 2 }} />
            3. Condiciones de Uso del Sitio Web
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            <strong>3.1. Acceso y Navegación</strong>
          </Typography>
          <Typography paragraph>
            El acceso a este sitio web es gratuito salvo en lo relativo al coste de la conexión a través de la red de telecomunicaciones suministrada por el proveedor de acceso contratado por el usuario.
          </Typography>

          <Typography paragraph>
            <strong>3.2. Uso Permitido</strong>
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Uso personal y no comercial"
                secondary="El sitio web está destinado exclusivamente para uso personal de las familias del centro educativo"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Información veraz"
                secondary="Los usuarios se comprometen a proporcionar información veraz y actualizada"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Respeto a terceros"
                secondary="No realizar acciones que puedan dañar, sobrecargar o deteriorar el sitio web"
              />
            </ListItem>
          </List>

          <Typography paragraph>
            <strong>3.3. Usos Prohibidos</strong>
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Queda expresamente prohibido el uso del sitio web para:
            </Typography>
          </Alert>
          <List>
            <ListItem>
              <ListItemText primary="• Realizar actividades ilícitas, fraudulentas o que infrinjan derechos de terceros" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Introducir virus informáticos o realizar ataques de denegación de servicio" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Intentar acceder a datos restringidos o cuentas de otros usuarios" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Utilizar el sitio para envío masivo de comunicaciones no solicitadas (spam)" />
            </ListItem>
          </List>
        </Paper>

        {/* Propiedad Intelectual */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <CopyrightIcon sx={{ mr: 2 }} />
            4. Propiedad Intelectual e Industrial
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            <strong>4.1. Derechos de Autor</strong>
          </Typography>
          <Typography paragraph>
            Todos los contenidos del sitio web (textos, imágenes, sonidos, bases de datos, diseños, código fuente, etc.) son propiedad de la AMPA del Bosc o de terceros que han autorizado su uso, y están protegidos por las leyes de propiedad intelectual.
          </Typography>

          <Typography paragraph>
            <strong>4.2. Marcas y Logotipos</strong>
          </Typography>
          <Box sx={{ bgcolor: Colors.background, p: 3, borderRadius: 2, mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Chip label="AMPA del Bosc" sx={{ bgcolor: Colors.primaryLight }} />
              <Chip label="Logotipo oficial" sx={{ bgcolor: Colors.accent }} />
              <Chip label="Identidad corporativa" sx={{ bgcolor: Colors.primaryDark, color: 'white' }} />
            </Stack>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Las marcas, nombres comerciales y logotipos que aparecen en el sitio web son propiedad de sus respectivos titulares.
            </Typography>
          </Box>

          <Typography paragraph>
            <strong>4.3. Uso Autorizado</strong>
          </Typography>
          <Typography paragraph>
            Se permite la visualización, descarga e impresión de contenidos únicamente para uso personal y privado. Cualquier otro uso requerirá autorización expresa por escrito.
          </Typography>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Fotografías:</strong> Las imágenes de menores publicadas cuentan con la autorización expresa de sus tutores legales conforme a la normativa de protección de datos.
            </Typography>
          </Alert>
        </Paper>

        {/* Responsabilidad */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <AccountBalanceIcon sx={{ mr: 2 }} />
            5. Limitación de Responsabilidad
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            <strong>5.1. Disponibilidad del Servicio</strong>
          </Typography>
          <Typography paragraph>
            La AMPA no garantiza la disponibilidad permanente del sitio web y no se responsabiliza de posibles daños derivados de fallos técnicos, interrupciones del servicio o problemas de conectividad.
          </Typography>

          <Typography paragraph>
            <strong>5.2. Contenidos de Terceros</strong>
          </Typography>
          <Typography paragraph>
            El sitio web puede contener enlaces a páginas de terceros. La AMPA no se responsabiliza del contenido, exactitud o servicios ofrecidos por dichas páginas externas.
          </Typography>

          <Typography paragraph>
            <strong>5.3. Exactitud de la Información</strong>
          </Typography>
          <Typography paragraph>
            Aunque se procura mantener actualizada toda la información, la AMPA no garantiza la exactitud, integridad o actualidad de los contenidos publicados.
          </Typography>

          <Alert severity="warning">
            <Typography variant="body2">
              <strong>Importante:</strong> La información sobre servicios, precios y horarios publicada en el sitio web tiene carácter informativo. Para contrataciones y confirmaciones, consulte siempre con la secretaría de la AMPA.
            </Typography>
          </Alert>
        </Paper>

        {/* Cookies y Tecnologías */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <WebIcon sx={{ mr: 2 }} />
            6. Cookies y Tecnologías de Seguimiento
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Este sitio web utiliza cookies y tecnologías similares para mejorar la experiencia del usuario. Para información detallada, consulte nuestra <strong>Política de Cookies</strong>.
          </Typography>

          <Box sx={{ bgcolor: Colors.background, p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
              Tipos de Cookies Utilizadas
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Cookies técnicas"
                  secondary="Necesarias para el funcionamiento básico del sitio web"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Cookies de preferencias"
                  secondary="Guardan las configuraciones personalizadas del usuario"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Cookies analíticas"
                  secondary="Analizan el uso del sitio web para mejorarlo (Google Analytics)"
                />
              </ListItem>
            </List>
          </Box>
        </Paper>

        {/* Legislación Aplicable */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <AccountBalanceIcon sx={{ mr: 2 }} />
            7. Legislación Aplicable y Jurisdicción
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Este aviso legal se rige por la legislación española. En particular:
          </Typography>

          <List>
            <ListItem>
              <ListItemText
                primary="Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio Electrónico"
                secondary="Normativa sobre comercio electrónico y servicios digitales"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Ley Orgánica 3/2018, de 5 de diciembre, de Protección de Datos Personales y garantía de los derechos digitales"
                secondary="Protección de datos personales (implementación del RGPD)"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Ley Orgánica 1/2002, de 22 de marzo, reguladora del Derecho de Asociación"
                secondary="Marco legal de funcionamiento de asociaciones"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Real Decreto Legislativo 1/2007, de 16 de noviembre, por el que se aprueba el texto refundido de la Ley General para la Defensa de los Consumidores y Usuarios"
                secondary="Protección de consumidores y usuarios"
              />
            </ListItem>
          </List>

          <Typography paragraph sx={{ mt: 3 }}>
            Para la resolución de cualquier controversia derivada del acceso o uso de este sitio web, las partes se someterán a los Juzgados y Tribunales de [Ciudad], renunciando expresamente a cualquier otro fuero que pudiera corresponderles.
          </Typography>
        </Paper>

        {/* Contacto */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <ContactMailIcon sx={{ mr: 2 }} />
            8. Contacto
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Para cualquier consulta sobre este aviso legal o sobre el sitio web en general:
          </Typography>

          <Box sx={{ bgcolor: Colors.background, p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
              📧 Información de Contacto
            </Typography>
            <Stack spacing={1}>
              <Typography><strong>Email general:</strong> ampadelbosc@gmail.com</Typography>
              <Typography><strong>Email legal:</strong> ampadelbosc@gmail.com</Typography>
              <Typography><strong>Teléfono:</strong> [Teléfono de contacto]</Typography>
              <Typography><strong>Dirección postal:</strong> [Dirección completa]</Typography>
              <Typography><strong>Horario de atención:</strong> Lunes a viernes de 9:00 a 17:00h</Typography>
            </Stack>
          </Box>
        </Paper>

        {/* Footer */}
        <Paper sx={{ p: 3, bgcolor: Colors.primaryLight, textAlign: 'center' }}>
          <Typography variant="body2" color={Colors.primaryDark}>
            Este aviso legal forma parte integrante de las condiciones de uso del sitio web.<br/>
            Su acceso y navegación implica la aceptación de los términos aquí establecidos.<br/>
            <strong>Última actualización:</strong> {new Date().toLocaleDateString('es-ES')}
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default LegalNotice;